// Series temporales de postulaciones, agrupadas por granularidad. Alimenta el
// gráfico de líneas del modal de Estadísticas de la pestaña "Formularios".
//
//   GET {APPLICANT}/application/stats?granularity=<g>[&contextFormId=<id>][&recruitmentSourceId=<a,b>]
//       [&clientId=<a,b>][&breakdown=recruitmentSource|client][&from=<s>][&to=<s>][&limit=<n>]
//   → { granularity, contextFormId, from, to, limit, maxLimit, stats: [{ date, total }], series, ... }
//
// Sin contextFormId ⇒ estadísticas generales (todos los formularios).
// Fuente y cliente aceptan varios ids separados por coma. Con `breakdown`, la
// respuesta trae además una serie por fuente o por cliente (`series`) con los
// mismos filtros y buckets que `stats`, para compararlas en el gráfico.
// `from`/`to` van en el MISMO formato que la granularidad (ver toRangeBound en
// el modal): años "YYYY", meses "YYYY-MM", días "YYYY-MM-DD", horas
// "YYYY-MM-DD HH", minutos "YYYY-MM-DD HH:MM".

export type StatsGranularity = "minutes" | "hours" | "days" | "months" | "years";
export type StatsBreakdown = "recruitmentSource" | "client";

// Clave con la que el backend agrupa las postulaciones sin fuente / sin cliente.
export const STATS_BREAKDOWN_NONE_KEY = "NONE";

// Debe mantenerse en sync con STATS_GRANULARITY_LIMITS del backend
// (job-application-service/src/shared/logic/application/getApplicationStats.ts).
export const STATS_LIMITS: Record<StatsGranularity, { default: number; max: number }> = {
  minutes: { default: 120, max: 1440 },
  hours:   { default: 48,  max: 720  },
  days:    { default: 30,  max: 365  },
  months:  { default: 12,  max: 60   },
  years:   { default: 10,  max: 50   },
};

export interface IStatPoint {
  date: string;
  total: number;
}

export interface IRecruitmentSourceStat {
  recruitmentSourceId: string | null;
  recruitmentSource: string;
  total: number;
}

export interface IStatSeries {
  key: string;
  label: string;
  total: number;
  points: IStatPoint[];
}

export interface IRecruitmentSourceOption {
  recruitmentSourceId: string;
  name: string;
  code?: string | null;
}

export interface IClientOption {
  clientId: string;
  name: string;
}

export interface IApplicationStatsResult {
  granularity: StatsGranularity;
  contextFormId: string | null;
  recruitmentSourceIds: string[];
  clientIds: string[];
  breakdown: StatsBreakdown | null;
  from: string | null;
  to: string | null;
  limit: number;
  maxLimit: number;
  stats: IStatPoint[];
  sourceDistribution: IRecruitmentSourceStat[];
  /** Postulantes distintos (RUT únicos) en el mismo universo que `stats`. */
  uniqueApplicants: number;
  /** Una serie por fuente o cliente cuando se pidió `breakdown`; si no, vacío. */
  series: IStatSeries[];
}

export const getApplicationStats = async (params: {
  granularity: StatsGranularity;
  contextFormId?: string;
  recruitmentSourceIds?: string[];
  clientIds?: string[];
  breakdown?: StatsBreakdown;
  from?: string;
  to?: string;
  limit?: number;
}): Promise<IApplicationStatsResult> => {
  const { granularity, contextFormId, recruitmentSourceIds, clientIds, breakdown, from, to, limit } = params;
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  if (!baseUrl) throw new Error("Configuración faltante: REACT_APP_API_APPLICANT.");

  const query = new URLSearchParams({ granularity });
  const userId = sessionStorage.getItem("usuarioId") ?? "";
  if (userId) query.set("userId", userId);
  if (contextFormId) query.set("contextFormId", contextFormId);
  if (recruitmentSourceIds?.length) query.set("recruitmentSourceId", recruitmentSourceIds.join(","));
  if (clientIds?.length) query.set("clientId", clientIds.join(","));
  if (breakdown) query.set("breakdown", breakdown);
  if (from) query.set("from", from);
  if (to) query.set("to", to);
  if (limit != null) query.set("limit", String(limit));

  const res = await fetch(`${baseUrl}/application/stats?${query.toString()}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  return (await res.json()) as IApplicationStatsResult;
};

export const getRecruitmentSources = async (): Promise<IRecruitmentSourceOption[]> => {
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  if (!baseUrl) throw new Error("Configuración faltante: REACT_APP_API_APPLICANT.");
  const res = await fetch(`${baseUrl}/catalogs/recruitment-sources`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data?.recruitmentSources) ? data.recruitmentSources : [];
};

// Clientes que tienen al menos un formulario de postulación creado. Alimenta el
// filtro de cliente del modal de Estadísticas (solo en estadísticas generales).
export const getClientsWithForms = async (): Promise<IClientOption[]> => {
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  if (!baseUrl) throw new Error("Configuración faltante: REACT_APP_API_APPLICANT.");
  const res = await fetch(`${baseUrl}/catalogs/clients-with-forms`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data?.clients) ? data.clients : [];
};
